import { format, isValid } from 'date-fns';
import { SecurityRead } from 'api';
import { ONE_HUNDRED_VALUE } from 'common/config/app';
import {
  DEFAULT_FALLBACK_VALUE,
  DEFAULT_SERIES_NAME,
  NEW_ROUND_ISSUE_PRICE_POST_TOOLTIP,
  NEW_ROUND_ISSUE_PRICE_PRE_TOOLTIP,
  NEW_ROUND_POST_MONEY_FDS_POST_TOOLTIP,
  NEW_ROUND_POST_MONEY_FDS_PRE_TOOLTIP,
  NEW_ROUND_PRF_POST_TOOLTIP,
  NEW_ROUND_PRF_PRE_TOOLTIP,
  OPR_DECIMAL_PLACES,
  POOL_REFRESH_MODE_PRE,
  SERIES_NAME_PREFIX,
} from 'common/constants/cap-table';
import { PREFERRED_STOCK, UNISSUED_OPTIONS } from 'common/constants/securityTypes';
import { CapTableInfo } from 'common/types/capTable/capTable';
import { Fund } from 'common/types/fund/fund';
import { getColumnIndex, getColumnLetter, toString } from 'utilities';
import { YEAR_MONTH_DAY_FORMAT } from 'utilities/slashFormat/constants';
import { NewRoundModelerComputed, NewRoundModelerData, PoolRefreshMode } from './NewRoundModeler.props';
import { PoolRefreshShares } from './ProformaCaptableDialog.props';

export const parseNumber = (v: string): number => {
  const stripped = v.replace(/[$,\s]/g, '');
  return Number.parseFloat(stripped) || 0;
};

export const getNextSeriesName = (securities: SecurityRead[] = []): string => {
  const seriesIndices = securities
    .filter(s => s.name?.startsWith(SERIES_NAME_PREFIX))
    .map(s => getColumnIndex(s.name!.slice(SERIES_NAME_PREFIX.length)))
    .filter((i): i is number => i !== null);
  if (seriesIndices.length === 0) {
    return DEFAULT_SERIES_NAME;
  }
  return `${SERIES_NAME_PREFIX}${getColumnLetter(Math.max(...seriesIndices) + 1)}`;
};

export const getCurrentSharesOutstanding = (sharesDiluted: CapTableInfo['shares_diluted']): number =>
  Number(sharesDiluted ?? DEFAULT_FALLBACK_VALUE) || 0;

const securityShareCount = (value: SecurityRead['shares_outstanding']): number =>
  Number(value ?? DEFAULT_FALLBACK_VALUE) || 0;

const isUnissuedOption = (security: SecurityRead): boolean =>
  toString(security.security_type) === toString(UNISSUED_OPTIONS);

// Imported pools often live on shares_outstanding. The cap table UI hides that
// row for unissued options and stores the pool on shares_diluted instead.
const unissuedPoolShares = (security: SecurityRead): number => {
  const outstanding = securityShareCount(security.shares_outstanding);
  if (outstanding > 0) {
    return outstanding;
  }
  return securityShareCount(security.shares_diluted);
};

export const getPremoneyUnallocatedOptions = (securities: SecurityRead[]): number =>
  securities.filter(isUnissuedOption).reduce((sum, security) => sum + unissuedPoolShares(security), 0);

// Cap table shares_diluted already includes an unissued pool entered as fully
// diluted shares. Current Shares Outstanding has to exclude that pool so fully
// diluted shares are outstanding plus the pool, not the pool counted twice.
export const getUnissuedSharesInDilutedTotal = (securities: SecurityRead[]): number =>
  securities
    .filter(isUnissuedOption)
    .reduce((sum, security) => sum + securityShareCount(security.shares_diluted), 0);

export const getDefaultOptionPoolRefresh = (cso: number, puo: number): string =>
  cso > 0
    ? ((puo / cso) * ONE_HUNDRED_VALUE).toFixed(OPR_DECIMAL_PLACES)
    : `${DEFAULT_FALLBACK_VALUE}.${DEFAULT_FALLBACK_VALUE.repeat(OPR_DECIMAL_PLACES)}`;

export const getDefaultFundId = (
  fundOwnership: CapTableInfo['fund_ownership'],
  fundList: Fund[] | undefined
): number | null => {
  const firstFodFund = fundOwnership?.fund_ownership_detail?.[0]?.fund;
  if (firstFodFund) {
    return firstFodFund;
  }
  return fundList?.[0]?.id ?? null;
};

const divideIfPositive = (numerator: number, denominator: number): number =>
  denominator > 0 ? numerator / denominator : 0;

const incrementalOptionPoolShares = (targetFraction: number, rawNumerator: number): number =>
  targetFraction > 0 && targetFraction < 1 ? Math.max(0, rawNumerator / (1 - targetFraction)) : 0;

const computePreMoneyRefresh = (
  opr: number,
  inv: number,
  p: number,
  pmFds: number,
  puo: number
): PoolRefreshShares => {
  const prf = opr * (1 + inv / p);
  const iops = incrementalOptionPoolShares(prf, prf * pmFds - puo);
  const adjustedPMFDS = pmFds + iops;
  const issuePrice = divideIfPositive(p, adjustedPMFDS);
  const sharesIssued = divideIfPositive(inv, issuePrice);
  return {
    poolRefreshFactor: prf,
    incrementalOptionPoolShares: iops,
    adjustedPremoneyFullyDilutedShares: adjustedPMFDS,
    newRoundIssuePrice: issuePrice,
    newRoundSharesIssued: sharesIssued,
    postMoneyFullyDilutedShares: adjustedPMFDS + sharesIssued,
  };
};

const computePostMoneyRefresh = (
  opr: number,
  inv: number,
  p: number,
  pmFds: number,
  puo: number
): PoolRefreshShares => {
  // Issue price uses pmFds (no pool adjustment), then solve for IOPS.
  // Goal: (puo + iops) / postFds = opr → iops = (opr*(pmFds+shares) - puo) / (1-opr)
  const issuePrice = divideIfPositive(p, pmFds);
  const sharesIssued = divideIfPositive(inv, issuePrice);
  const iops = incrementalOptionPoolShares(opr, opr * (pmFds + sharesIssued) - puo);
  return {
    poolRefreshFactor: opr,
    incrementalOptionPoolShares: iops,
    adjustedPremoneyFullyDilutedShares: pmFds,
    newRoundIssuePrice: issuePrice,
    newRoundSharesIssued: sharesIssued,
    postMoneyFullyDilutedShares: pmFds + sharesIssued + iops,
  };
};

export const computeNewRoundModeler = ({
  preMoney,
  amountInvested,
  optionPoolRefresh,
  poolRefreshMode,
  fundInvestment,
  cso,
  puo,
}: {
  preMoney: string;
  amountInvested: string;
  optionPoolRefresh: string;
  poolRefreshMode: PoolRefreshMode;
  fundInvestment: string;
  cso: number;
  puo: number;
}): NewRoundModelerComputed | null => {
  const p = parseNumber(preMoney);
  const inv = parseNumber(amountInvested);
  const opr = Number.parseFloat(optionPoolRefresh) / ONE_HUNDRED_VALUE;
  const fi = parseNumber(fundInvestment);

  if (p <= 0 || inv <= 0) {
    return null;
  }

  const pmFds = cso + puo;
  const refreshShares =
    poolRefreshMode === POOL_REFRESH_MODE_PRE
      ? computePreMoneyRefresh(opr, inv, p, pmFds, puo)
      : computePostMoneyRefresh(opr, inv, p, pmFds, puo);

  return {
    currentSharesOutstanding: cso,
    premoneyUnallocatedOptions: puo,
    premoneyFullyDilutedShares: pmFds,
    ...refreshShares,
    postMoneyValuation: p + inv,
    fundOwnershipPct: divideIfPositive(fi, inv),
    numberOfShares: divideIfPositive(fi, refreshShares.newRoundIssuePrice),
    dilutionToExistingHolders:
      refreshShares.postMoneyFullyDilutedShares > 0
        ? pmFds / refreshShares.postMoneyFullyDilutedShares - 1
        : 0,
  };
};

export const hasExistingPreferredSecurity = (securities: SecurityRead[]): boolean =>
  securities.some(s => s.security_type === PREFERRED_STOCK && !s.is_deleted);

export const isSecurityNameTaken = (securities: SecurityRead[], securityName: string): boolean => {
  const trimmedName = securityName.trim().toLowerCase();
  if (!trimmedName) {
    return false;
  }
  return securities.some(s => !s.is_deleted && s.name?.trim().toLowerCase() === trimmedName);
};

export const isNewRoundModelerValid = ({
  expanded,
  preMoney,
  amountInvested,
  fundInvestment,
  optionPoolRefresh,
  securityName,
  isNameTaken,
  hasPreferredSecurity,
  fundId,
  investmentDate,
}: {
  expanded: boolean;
  preMoney: string;
  amountInvested: string;
  fundInvestment: string;
  optionPoolRefresh: string;
  securityName: string;
  isNameTaken: boolean;
  hasPreferredSecurity: boolean;
  fundId: number | null;
  investmentDate: Date | null;
}): boolean => {
  if (!expanded) {
    return true;
  }
  const p = parseNumber(preMoney);
  const inv = parseNumber(amountInvested);
  const fi = parseNumber(fundInvestment);
  const opr = Number.parseFloat(optionPoolRefresh);
  const dateOk = investmentDate !== null && isValid(investmentDate);
  return (
    p > 0 &&
    inv > 0 &&
    fi > 0 &&
    fi <= inv &&
    opr >= 0 &&
    securityName.trim().length > 0 &&
    !isNameTaken &&
    hasPreferredSecurity &&
    fundId !== null &&
    dateOk
  );
};

// Matches the generated `cell_annotations` shape on SecurityRead / SharesAcquisitionWritable / etc.
export type CellAnnotationsWireMap = Record<string, Record<string, {
  row_mode?: 'single_cell' | 'single_row' | 'multi_row';
  columns_config?: Array<unknown>;
  rows?: Array<{ position?: number; value?: string | null; metadata?: unknown }>;
}>>;

// Single-cell PRE_FX_CURRENCY annotation; backend FX-converts rows[0] to the host currency.
export const buildPreFxCellAnnotations = (fieldAlias: string, sourceAmountUSD: number): CellAnnotationsWireMap => ({
  [fieldAlias]: {
    pre_fx_currency: {
      row_mode: 'single_cell',
      columns_config: [],
      rows: [{ position: 0, value: String(sourceAmountUSD), metadata: { source_currency: 'USD' } }],
    },
  },
});

export const isFundInvestmentExceedingAmountInvested = (fundInvestment: string, amountInvested: string): boolean => {
  const fi = parseNumber(fundInvestment);
  const inv = parseNumber(amountInvested);
  return fi > 0 && inv > 0 && fi > inv;
};

export const formatInvestmentDate = (investmentDate: Date | null): string => {
  if (!investmentDate || !isValid(investmentDate)) {
    return '';
  }
  return format(investmentDate, YEAR_MONTH_DAY_FORMAT);
};

export const buildNewRoundModelerData = ({
  expanded,
  isModelerValid,
  preMoney,
  amountInvested,
  optionPoolRefresh,
  poolRefreshMode,
  securityName,
  investmentDateStr,
  fundId,
  fundInvestment,
  computed,
}: {
  expanded: boolean;
  isModelerValid: boolean;
  preMoney: string;
  amountInvested: string;
  optionPoolRefresh: string;
  poolRefreshMode: PoolRefreshMode;
  securityName: string;
  investmentDateStr: string;
  fundId: number | null;
  fundInvestment: string;
  computed: NewRoundModelerComputed | null;
}): NewRoundModelerData => ({
  isEnabled: expanded,
  isValid: isModelerValid,
  preMoney: parseNumber(preMoney),
  amountInvested: parseNumber(amountInvested),
  optionPoolRefreshDecimal: Number.parseFloat(optionPoolRefresh) / ONE_HUNDRED_VALUE,
  poolRefreshMode,
  securityName,
  investmentDate: investmentDateStr,
  fundId,
  fundInvestment: parseNumber(fundInvestment),
  computed,
});

export const getIssuePriceTooltip = (poolRefreshMode: PoolRefreshMode): string =>
  poolRefreshMode === POOL_REFRESH_MODE_PRE ? NEW_ROUND_ISSUE_PRICE_PRE_TOOLTIP : NEW_ROUND_ISSUE_PRICE_POST_TOOLTIP;

export const getPrfTooltip = (poolRefreshMode: PoolRefreshMode): string =>
  poolRefreshMode === POOL_REFRESH_MODE_PRE ? NEW_ROUND_PRF_PRE_TOOLTIP : NEW_ROUND_PRF_POST_TOOLTIP;

export const getPostFdsTooltip = (poolRefreshMode: PoolRefreshMode): string =>
  poolRefreshMode === POOL_REFRESH_MODE_PRE
    ? NEW_ROUND_POST_MONEY_FDS_PRE_TOOLTIP
    : NEW_ROUND_POST_MONEY_FDS_POST_TOOLTIP;

